// FUNCTIONALITY-115C — Search normalization V5 (extends V4 precision-first).
// One shared engine for product/category/brand/SKU search.
// Rule: NO RESULT IS BETTER THAN A WRONG RESULT.

import {
  generateBrandVariants,
  isProtectedNumericQuery,
  normalizeBrandQuery,
  resolveCuratedBrandAlias,
  scoreBrandMatch,
} from "./brand-normalization-v2.js";
import { normalizeIdentifier } from "./product-identity-v2.js";
import {
  expandSearchQueryAliases,
  evidenceNeedlesForQuery,
  resolveAliasFamilyForQuery,
  ensureOmnisearchAliasesLoaded,
} from "./search-aliases-v2.js";

export const SEARCH_MODE_LABEL = "precision_normalized_v5" as const;

// R2-R6 — warm candidate omnisearch aliases without blocking first paint.
void ensureOmnisearchAliasesLoaded();

const MIN_ACCEPT_SCORE = 62;
const MIN_MARGIN = 8;
const SHORT_QUERY_MAX = 3;

/** Soft query suggestions — never auto-force results. User must confirm. */
const QUERY_SUGGESTIONS: Array<{ from: string[]; to: string; evidenceNeedles: string[] }> = [
  {
    // Typo / layout: свк → сік (juice). Explicitly NOT CBD/DBC.
    from: ["свк", "svk"],
    to: "сік",
    evidenceNeedles: ["сік", "сок", "juice", "напій", "напиток"],
  },
];

/** Business-approved category aliases (never global fuzzy). свк removed — see QUERY_SUGGESTIONS. */
const CATEGORY_ALIASES: Array<{
  canonical: string;
  aliases: string[];
  /** Title/category evidence required for a hit. */
  evidenceNeedles: string[];
  categoryKeys?: string[];
}> = [
  {
    canonical: "CBD/DBC",
    aliases: ["cbd", "dbc", "сбд", "кбд", "cbd/dbc", "dbc/cbd"],
    evidenceNeedles: ["cbd", "dbc", "hemp", "конопл", "cannab", "сбд", "кбд"],
  },
];

const ZERO_RESULT_PHRASES_UA = [
  "🥢 Навіть мудрий кухар інколи змінює запит. Спробуйте назву, бренд, категорію або SKU.",
  "🍜 Каталог мовчить… але аромат підказок поруч. Спробуйте назву, бренд чи SKU.",
  "🍙 Нічого не знайшли. Може, інша назва, бренд, категорія або код товару?",
] as const;

const ZERO_RESULT_PHRASES_EN = [
  "🥢 Even a wise cook sometimes rephrases the ask. Try a name, brand, category, or SKU.",
  "🍜 The catalog is quiet… try another name, brand, or product code.",
  "🍙 Nothing matched. Try a product name, brand, category, or SKU.",
] as const;

export function pickZeroResultPhrase(lang: "ua" | "en", seed = ""): string {
  const list = lang === "en" ? ZERO_RESULT_PHRASES_EN : ZERO_RESULT_PHRASES_UA;
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return list[h % list.length]!;
}

function querySuggestionHit(query: string): (typeof QUERY_SUGGESTIONS)[number] | null {
  const folded = foldToken(query);
  const compact = compactToken(query);
  for (const row of QUERY_SUGGESTIONS) {
    if (row.from.some((a) => foldToken(a) === folded || compactToken(a) === compact)) {
      return row;
    }
  }
  return null;
}

function hammingDistance(a: string, b: string): number {
  if (a.length !== b.length) return Number.POSITIVE_INFINITY;
  let d = 0;
  for (let i = 0; i < a.length; i += 1) if (a[i] !== b[i]) d += 1;
  return d;
}

/** True for phones / order numbers / prices — never SKU-fuzz these query shapes. */
function isNonSkuNumericQuery(query: string): boolean {
  const raw = String(query ?? "").trim();
  if (/^STG-/i.test(raw)) return true;
  if (/[.,₴$]|uah/i.test(raw)) return true;
  const compact = raw.replace(/[\s\-()/]+/g, "");
  // UA phones: +380… / 380… / 0XXXXXXXXX
  if (/^\+?380\d{9}$/.test(compact)) return true;
  if (/^0\d{9}$/.test(compact)) return true;
  // Long chat/order ids (not typical product SKU length)
  if (/^\d{13,}$/.test(compact)) return true;
  return false;
}

/** Numeric SKU with exactly one wrong digit → unique suggestion only. Never auto-open. */
export function suggestSkuOneDigitTypo(
  query: string,
  products: Array<{ sku?: string | null }>,
): string | null {
  const q = String(query ?? "").trim();
  // Same length digit SKUs only; never fuzz phones / order ids / prices.
  if (!/^\d{6,12}$/.test(q) || isNonSkuNumericQuery(q)) return null;
  const matches = new Set<string>();
  for (const p of products) {
    const sku = String(p.sku ?? "").trim();
    if (!/^\d+$/.test(sku) || sku.length !== q.length) continue;
    if (hammingDistance(q, sku) === 1) matches.add(sku);
    if (matches.size > 1) return null;
  }
  if (matches.size !== 1) return null;
  return [...matches][0] ?? null;
}

const SYNONYM_GROUPS: string[][] = [
  ["том ям", "томям", "tom yam", "tomyam", "tom yum", "tomyum"],
  ["рамен", "ramen", "рамени", "рамэн"],
  ["бульдак", "buldak", "boldak", "булдак"],
  ["нори", "nori"],
  ["кимчи", "кімчі", "kimchi"],
  ["кокосове молоко", "coconut milk", "coconutmilk", "кокосовое молоко"],
  ["соевий соус", "соєвий соус", "soy sauce", "soysauce", "соевый соус"],
  ["рисова локшина", "rice noodles", "ricenoodles", "рисовая лапша"],
  ["мисо", "місо", "miso"],
  ["васаби", "wasabi"],
  ["удон", "udon"],
  ["соба", "soba"],
  ["чили", "чілі", "chili", "chilli"],
  ["чипси", "chips", "чипсы"],
  ["чай", "tea"],
  ["кіндер", "киндер", "kinder", "кіндеп", "киндеп", "кін", "кин"],
];

export function foldToken(raw: string): string {
  return String(raw ?? "")
    .normalize("NFKC")
    .trim()
    .toLowerCase()
    .replace(/[’'`´]/g, "'")
    .replace(/[‐‑‒–—―]/g, "-")
    .replace(/\s+/g, " ")
    .replace(/і/g, "и")
    .replace(/ї/g, "и")
    .replace(/є/g, "е")
    .replace(/ґ/g, "г")
    .replace(/ы/g, "и")
    .replace(/ё/g, "е");
}

export function compactToken(raw: string): string {
  return foldToken(raw).replace(/[\s\-_'’.,:;!?]+/g, "");
}

function synonymExactHit(token: string): string[] {
  const folded = foldToken(token);
  const compact = compactToken(token);
  if (!folded || folded.length < 2) return [];
  const out = new Set<string>();
  for (const group of SYNONYM_GROUPS) {
    const hit = group.some((g) => {
      const gf = foldToken(g);
      const gc = compactToken(g);
      // Exact / compact only — never substring (prevents собака → соба/soba).
      return gf === folded || gc === compact;
    });
    if (!hit) continue;
    for (const g of group) {
      out.add(foldToken(g));
      out.add(compactToken(g));
    }
  }
  return [...out];
}

function categoryAliasHit(query: string): (typeof CATEGORY_ALIASES)[number] | null {
  const folded = foldToken(query);
  const compact = compactToken(query);
  for (const row of CATEGORY_ALIASES) {
    if (row.aliases.some((a) => foldToken(a) === folded || compactToken(a) === compact)) {
      return row;
    }
  }
  return null;
}

function latinizeForSearch(value: string): string {
  const map: Record<string, string> = {
    а: "a",
    б: "b",
    в: "v",
    г: "h",
    ґ: "g",
    д: "d",
    е: "e",
    є: "ye",
    ж: "zh",
    з: "z",
    и: "y",
    і: "i",
    ї: "yi",
    й: "y",
    к: "k",
    л: "l",
    м: "m",
    н: "n",
    о: "o",
    п: "p",
    р: "r",
    с: "s",
    т: "t",
    у: "u",
    ф: "f",
    х: "kh",
    ц: "ts",
    ч: "ch",
    ш: "sh",
    щ: "shch",
    ь: "",
    ю: "yu",
    я: "ya",
    ы: "y",
    э: "e",
    ё: "yo",
    ъ: "",
  };
  // Preserve Ukrainian і→i (do not run foldToken which maps і→и).
  const raw = String(value ?? "")
    .normalize("NFKC")
    .toLowerCase()
    .replace(/[\s\-_'’.,:;!?]+/g, "");
  return [...raw].map((ch) => map[ch] ?? ch).join("");
}

function translitCompactToLatin(value: string): string {
  return latinizeForSearch(value);
}

function brandTokenCompacts(brand: string | null | undefined): string[] {
  const raw = String(brand ?? "").trim();
  if (!raw) return [];
  const out = new Set<string>();
  const compact = compactToken(raw);
  if (compact) out.add(compact);
  const latin = translitCompactToLatin(raw);
  if (latin) out.add(latin);
  for (const part of raw.split(/[\s|/·•,;]+/)) {
    const c = compactToken(part);
    if (c.length >= 2) out.add(c);
    const l = translitCompactToLatin(part);
    if (l.length >= 2) out.add(l);
  }
  return [...out];
}

function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const row = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i += 1) {
    let prev = i - 1;
    row[0] = i;
    for (let j = 1; j <= b.length; j += 1) {
      const cur = row[j]!;
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      row[j] = Math.min(row[j]! + 1, row[j - 1]! + 1, prev + cost);
      prev = cur;
    }
  }
  return row[b.length]!;
}

export function tokenizeQuery(q: string): string[] {
  if (isProtectedNumericQuery(q)) {
    const compact = compactToken(q);
    return compact ? [compact, foldToken(q)] : [foldToken(q)];
  }
  const folded = foldToken(q);
  const compactWhole = compactToken(q);
  const parts = folded
    .split(/[^a-z0-9а-я]+/i)
    .map((t) => t.trim())
    .filter((t) => t.length >= 2);

  const units = new Set<string>();
  if (compactWhole.length >= 2) units.add(compactWhole);
  if (folded) units.add(folded);
  for (const variant of generateBrandVariants(q)) {
    units.add(foldToken(variant));
    units.add(compactToken(variant));
  }
  const brandAlias = resolveCuratedBrandAlias(q);
  if (brandAlias) {
    units.add(foldToken(brandAlias));
    units.add(compactToken(brandAlias));
  }
  for (const p of parts) {
    units.add(p);
    units.add(compactToken(p));
    for (const s of synonymExactHit(p)) units.add(s);
  }
  for (const alias of expandSearchQueryAliases(q)) {
    units.add(foldToken(alias));
    units.add(compactToken(alias));
  }
  for (const needle of evidenceNeedlesForQuery(q)) {
    units.add(foldToken(needle));
    units.add(compactToken(needle));
  }
  for (let i = 0; i < parts.length - 1; i++) {
    const bigram = `${parts[i]} ${parts[i + 1]}`;
    units.add(foldToken(bigram));
    units.add(compactToken(bigram));
    for (const s of synonymExactHit(bigram)) units.add(s);
  }
  return [...units].filter(Boolean).slice(0, 32);
}

/** Build the folded haystack once per product (title + ru + category + identifiers + aliases). */
export function buildSearchHaystack(input: {
  title: string;
  titleRu?: string | null;
  categoryName?: string | null;
  sku?: string | null;
  brand?: string | null;
  country?: string | null;
  barcode?: string | null;
  article?: string | null;
  promProductId?: string | null;
  aliases?: string[] | null;
  /** Compact Customer-safe summary (optional). */
  shortDescription?: string | null;
  /**
   * AP3R3 — prebuilt search document (may include capped full_description tokens).
   * Used for indexing only; not rendered on ProductV2 cards.
   */
  searchText?: string | null;
}): string {
  return foldToken(
    [
      input.title,
      input.titleRu,
      input.categoryName,
      input.sku,
      input.article,
      input.barcode,
      input.promProductId,
      input.brand,
      input.country,
      input.shortDescription,
      input.searchText,
      ...(input.aliases ?? []),
    ]
      .filter(Boolean)
      .join(" "),
  );
}

export interface SearchableProduct {
  id: string;
  sku: string;
  barcode?: string | null;
  merchantSku?: string | null;
  merchantArticle?: string | null;
  barcodeGtin?: string | null;
  promProductId?: string | null;
  manufacturerPartNumber?: string | null;
  titleAliases?: string[] | null;
  title: string;
  titleRu?: string | null;
  brand?: string | null;
  categoryName?: string | null;
  categoryKeys?: string[];
  search: string;
  description?: string | null;
  /** SEARCH-PERF-169A — optional thumb for suggestion rows (never full-grid hydrate). */
  imageUrl?: string | null;
  /** COMMERCE-176C-R1 — optional price for suggestion rows. */
  priceUah?: number | null;
}

export interface SearchHit {
  productId: string;
  score: number;
  matchedField:
    | "sku"
    | "merchant_sku"
    | "merchant_article"
    | "barcode"
    | "manufacturer_part_number"
    | "prom_product_id"
    | "title"
    | "brand"
    | "category"
    | "alias"
    | "prefix"
    | "layout"
    | "fuzzy"
    | "description";
  matchedAlias?: string;
  correctionType?: string;
  identifierType?: string;
}

export interface SearchDiagnostics {
  original_query: string;
  normalized_query: string;
  generated_variants_count: number;
  matched_field: string | null;
  matched_alias: string | null;
  correction_type: string | null;
  final_confidence: number;
  result_count: number;
  zero_result_reason: string | null;
}

export interface SearchResult {
  hits: SearchHit[];
  products: SearchableProduct[];
  didYouMean: string | null;
  /** SKU typo suggestion copy key payload (exact SKU string). */
  skuSuggestion: string | null;
  zeroResultPhraseKey: "creative" | null;
  diagnostics: SearchDiagnostics;
}

function idExact(query: string, value: string | null | undefined): boolean {
  const v = String(value ?? "").trim();
  if (!v) return false;
  const qRaw = String(query ?? "").trim();
  const qNorm = normalizeIdentifier(qRaw);
  const vNorm = normalizeIdentifier(v);
  return qRaw === v || qNorm === vNorm || compactToken(qRaw) === compactToken(v);
}

function scoreProduct(query: string, product: SearchableProduct): SearchHit | null {
  const qRaw = String(query ?? "").trim();
  if (!qRaw) return null;
  const qFold = foldToken(qRaw);
  const qCompact = compactToken(qRaw);
  const merchantSkuRaw = String(product.merchantSku ?? "").trim();
  const merchantArticleRaw = String(product.merchantArticle ?? "").trim();
  const barcode = String(product.barcodeGtin ?? product.barcode ?? "").trim();
  const originalSku = String(product.manufacturerPartNumber ?? "").trim();
  const promId = String(product.promProductId ?? product.id ?? "").trim();
  const legacySku = String(product.sku ?? "").trim();
  // CATALOG-SYNC-138: Prom.sku may equal barcode; AS-hash never counts as store SKU.
  const isAs = (v: string) => /^AS[0-9A-Z]{8}$/i.test(v);
  const storeSku = merchantSkuRaw && !isAs(merchantSkuRaw) ? merchantSkuRaw : "";
  const storeArticle = merchantArticleRaw && !isAs(merchantArticleRaw) ? merchantArticleRaw : "";
  const titleFold = foldToken(product.title);
  const titleCompact = compactToken(product.title);
  const titleRuFold = foldToken(product.titleRu ?? "");
  const brandFold = foldToken(product.brand ?? "");
  const brandCompact = compactToken(product.brand ?? "");
  const catFold = foldToken(product.categoryName ?? "");
  const hay = product.search || buildSearchHaystack(product);
  const hayCompact = compactToken(hay);
  const descFold = foldToken(product.description ?? "");

  // 1) Exact identifier matches — highest; Prom id is compatibility alias, not customer SKU.
  if (storeSku && idExact(qRaw, storeSku)) {
    return {
      productId: product.id,
      score: 100,
      matchedField: "merchant_sku",
      identifierType: "merchant_sku",
    };
  }
  if (storeArticle && idExact(qRaw, storeArticle)) {
    return {
      productId: product.id,
      score: 100,
      matchedField: "merchant_article",
      identifierType: "merchant_article",
    };
  }
  if (originalSku && originalSku !== barcode && idExact(qRaw, originalSku)) {
    return {
      productId: product.id,
      score: 100,
      matchedField: "manufacturer_part_number",
      identifierType: "manufacturer_part_number",
    };
  }
  if (idExact(qRaw, barcode)) {
    return {
      productId: product.id,
      score: 99,
      matchedField: "barcode",
      identifierType: "barcode",
    };
  }
  if (idExact(qRaw, promId)) {
    return {
      productId: product.id,
      score: 98,
      matchedField: "prom_product_id",
      identifierType: "prom_product_id",
      correctionType: "prom_id_alias",
    };
  }
  // Legacy sku field only when it is not merely the Prom id and not the barcode.
  if (legacySku && legacySku !== promId && legacySku !== barcode && idExact(qRaw, legacySku)) {
    return {
      productId: product.id,
      score: 100,
      matchedField: "sku",
      identifierType: "merchant_sku",
    };
  }

  // 1b) Title aliases (місо/мисо/miso) — exact alias token, not fuzzy rewrite.
  const aliases = product.titleAliases ?? [];
  if (aliases.some((a) => idExact(qRaw, a) || qFold === foldToken(a))) {
    return {
      productId: product.id,
      score: 92,
      matchedField: "alias",
      matchedAlias: qRaw,
      correctionType: "title_alias",
      identifierType: "text",
    };
  }

  // 2) Product-family aliases (палочки → chopsticks evidence). Homonym-safe: a
  // family's plain evidenceNeedles (e.g. "палич"/"палоч" also appear in unrelated
  // snack "sticks" products) only count together with disambiguationContext;
  // strongEvidenceNeedles (e.g. "chopstick", "хасі") are unambiguous on their own.
  const aliasFamily = resolveAliasFamilyForQuery(qRaw);
  if (aliasFamily) {
    const evidence = foldToken(
      [
        product.title,
        product.titleRu,
        product.categoryName,
        product.brand,
        ...(product.categoryKeys ?? []),
        ...(product.titleAliases ?? []),
      ].join(" "),
    );
    const strongHit = (aliasFamily.strongEvidenceNeedles ?? []).some((n) => evidence.includes(foldToken(n)));
    const weakHit = aliasFamily.evidenceNeedles.some((n) => evidence.includes(foldToken(n)));
    const hasContextGate = (aliasFamily.disambiguationContext ?? []).length > 0;
    const contextHit = !hasContextGate || aliasFamily.disambiguationContext!.some((n) => evidence.includes(foldToken(n)));
    const needleHit = strongHit || (weakHit && contextHit);
    if (needleHit) {
      const expanded = expandSearchQueryAliases(qRaw);
      return {
        productId: product.id,
        // Score sits alongside curated category aliases (>= 88) so that legitimate
        // multi-product family matches (e.g. many "рамен" products) are not
        // rejected by the ambiguous-tie margin guard below (see MIN_MARGIN).
        score: 88,
        matchedField: "alias",
        matchedAlias: expanded.find((a) => a !== qRaw.toLocaleLowerCase("uk")) ?? expanded[0] ?? qRaw,
        correctionType: "product_alias_family",
      };
    }
    return null;
  }

  // 3) Curated category aliases (CBD/DBC evidence only; свк is NOT an alias).
  const catAlias = categoryAliasHit(qRaw);
  if (catAlias) {
    const evidence = foldToken(
      [product.title, product.titleRu, product.categoryName, product.brand, ...(product.categoryKeys ?? [])].join(" "),
    );
    const needleHit = catAlias.evidenceNeedles.some((n) => evidence.includes(foldToken(n)));
    const keyHit = (catAlias.categoryKeys ?? []).some((k) => (product.categoryKeys ?? []).includes(k));
    if (needleHit || keyHit) {
      return {
        productId: product.id,
        score: 88,
        matchedField: "alias",
        matchedAlias: catAlias.canonical,
        correctionType: "category_alias",
      };
    }
    return null;
  }

  // Short queries (2–3 chars): brand/title prefix suggestions — no global fuzzy.
  // COMMERCE-176C-R1 — boost brand exact/prefix; do not treat Cyrillic→Latin as broad EN title match.
  // Avoid substring false-positives (кін ⊂ кіноа).
  const isShort = qCompact.length > 0 && qCompact.length <= SHORT_QUERY_MAX;
  if (isShort && !isProtectedNumericQuery(qRaw)) {
    const brandAlias = resolveCuratedBrandAlias(qRaw);
    if (brandAlias && product.brand && normalizeBrandQuery(brandAlias) === normalizeBrandQuery(product.brand)) {
      return {
        productId: product.id,
        score: 88,
        matchedField: "brand",
        matchedAlias: brandAlias,
        correctionType: "short_brand_alias",
      };
    }
    const brandTokens = brandTokenCompacts(product.brand);
    const titleWords = titleFold.split(/[^a-z0-9а-я]+/i).filter((w) => w.length >= 2);
    // Brand compact prefix via keyboard-layout / curated variants only (not phonetic EN sweep).
    const brandPrefixHit =
      qCompact.length >= 2 &&
      brandTokens.some((t) => t.startsWith(qCompact) || qCompact.startsWith(t.slice(0, Math.min(t.length, qCompact.length))) && t.length >= 4 && qCompact.length >= 3);
    if (brandPrefixHit || (product.brand && scoreBrandMatch(qRaw, product.brand) === "prefix")) {
      return { productId: product.id, score: 86, matchedField: "prefix", correctionType: "short_brand_prefix" };
    }
    // Title WORD prefix — prefer longer words; require real word start (not mid-token).
    let bestWord = 0;
    for (const w of titleWords) {
      const wc = compactToken(w);
      if (wc.startsWith(qCompact) && wc.length > qCompact.length) bestWord = Math.max(bestWord, wc.length);
    }
    if (bestWord >= qCompact.length + 3) {
      return {
        productId: product.id,
        score: 80,
        matchedField: "prefix",
        correctionType: "short_title_word_prefix",
      };
    }
    if (bestWord > qCompact.length) {
      return {
        productId: product.id,
        score: 70,
        matchedField: "prefix",
        correctionType: "short_title_edge",
      };
    }
    const synShort = synonymExactHit(qRaw);
    if (
      synShort.length > 0 &&
      brandTokens.some((t) =>
        synShort.some((s) => {
          const sc = compactToken(s);
          return t === sc || t.startsWith(sc) || (sc.length >= 4 && sc.startsWith(t));
        }),
      )
    ) {
      return { productId: product.id, score: 84, matchedField: "alias", correctionType: "short_alias" };
    }
    return null;
  }

  // 3) Exact full / public title.
  if (qFold === titleFold || qCompact === titleCompact) {
    return { productId: product.id, score: 95, matchedField: "title" };
  }
  if (titleRuFold && qFold === titleRuFold) {
    return { productId: product.id, score: 94, matchedField: "title" };
  }

  // 4) Exact / curated brand.
  const brandAlias = resolveCuratedBrandAlias(qRaw);
  const brandReason = product.brand ? scoreBrandMatch(qRaw, product.brand) : null;
  if (brandAlias && product.brand && normalizeBrandQuery(brandAlias) === normalizeBrandQuery(product.brand)) {
    return {
      productId: product.id,
      score: 90,
      matchedField: "brand",
      matchedAlias: brandAlias,
      correctionType: "brand_alias",
    };
  }
  if (brandReason === "exact_normalized" || brandReason === "exact_canonical") {
    return { productId: product.id, score: 90, matchedField: "brand" };
  }
  if (brandReason === "alias" || brandReason === "phonetic") {
    return {
      productId: product.id,
      score: 86,
      matchedField: "alias",
      ...(product.brand ? { matchedAlias: product.brand } : {}),
      correctionType: brandReason,
    };
  }
  if (brandReason === "keyboard" || brandReason === "transliteration") {
    return {
      productId: product.id,
      score: 80,
      matchedField: "layout",
      ...(product.brand ? { matchedAlias: product.brand } : {}),
      correctionType: brandReason,
    };
  }
  if (brandReason === "prefix") {
    return {
      productId: product.id,
      score: 72,
      matchedField: "prefix",
      ...(product.brand ? { matchedAlias: product.brand } : {}),
    };
  }

  // 5) Exact category name.
  if (catFold && (qFold === catFold || catFold.includes(qFold) && qFold.length >= 4)) {
    return { productId: product.id, score: 78, matchedField: "category" };
  }

  // 6) Title token prefix / coverage for multi-word.
  const queryParts = qFold.split(/[^a-z0-9а-я]+/i).filter((t) => t.length >= 2);
  if (queryParts.length >= 2) {
    const strong = queryParts.filter(
      (part) =>
        titleFold.includes(part) ||
        brandFold.includes(part) ||
        catFold.includes(part) ||
        hay.includes(part),
    );
    // Require meaningful coverage + at least one strong title/brand/category match.
    const titleOrBrand =
      queryParts.some((p) => titleFold.includes(p) || brandFold.includes(p) || catFold.includes(p));
    if (titleOrBrand && strong.length >= Math.ceil(queryParts.length * 0.6)) {
      return { productId: product.id, score: 74, matchedField: "title", correctionType: "token_coverage" };
    }
    // Weak description-only must not win.
    const descOnly =
      !titleOrBrand &&
      queryParts.every((p) => descFold.includes(p)) &&
      queryParts.some((p) => descFold.includes(p));
    if (descOnly) {
      return { productId: product.id, score: 35, matchedField: "description", correctionType: "description_only" };
    }
    return null;
  }

  // 7) One-word: synonym exact, title prefix, conservative fuzzy.
  const syn = synonymExactHit(qRaw);
  if (syn.some((s) => hay.includes(s) || hayCompact.includes(compactToken(s)))) {
    // Prefer title/brand evidence over description.
    if (titleFold.includes(qFold) || brandFold.includes(qFold) || syn.some((s) => titleFold.includes(s))) {
      return { productId: product.id, score: 76, matchedField: "alias", correctionType: "synonym" };
    }
  }

  if (qCompact.length >= 4 && (titleCompact.startsWith(qCompact) || hayCompact.includes(qCompact))) {
    if (titleCompact.includes(qCompact) || brandCompact.includes(qCompact)) {
      return { productId: product.id, score: 70, matchedField: "prefix" };
    }
  }

  // Conservative fuzzy — brand tokens + transliteration; dist=1 accepted for commerce typos.
  if (qCompact.length >= 5 && !isProtectedNumericQuery(qRaw)) {
    const qLatin = translitCompactToLatin(qRaw);
    const targets = [
      ...brandTokenCompacts(product.brand),
      brandCompact,
      titleCompact.slice(0, Math.max(qCompact.length + 2, 12)),
    ].filter((t) => t.length >= 4);
    for (const target of targets) {
      for (const probe of [qCompact, qLatin].filter((p) => p.length >= 5)) {
        const window = target.slice(0, Math.max(probe.length, Math.min(target.length, probe.length + 2)));
        const dist = levenshtein(probe, window);
        const maxDist = Math.max(1, Math.floor(Math.min(probe.length, window.length) / 5));
        if (dist > 0 && dist <= maxDist) {
          return {
            productId: product.id,
            score: dist === 1 ? 68 : 64,
            matchedField: "fuzzy",
            correctionType: "conservative_fuzzy",
          };
        }
      }
    }
  }

  // Description fallback — low score only.
  if (qFold.length >= 4 && descFold.includes(qFold)) {
    return { productId: product.id, score: 38, matchedField: "description" };
  }

  return null;
}

function emptySearch(
  original: string,
  normalized: string,
  variantsCount: number,
  reason: string | null,
  extras?: {
    didYouMean?: string | null;
    skuSuggestion?: string | null;
    confidence?: number;
    correctionType?: string | null;
    zeroResultPhraseKey?: "creative" | null;
  },
): SearchResult {
  return {
    hits: [],
    products: [],
    didYouMean: extras?.didYouMean ?? null,
    skuSuggestion: extras?.skuSuggestion ?? null,
    zeroResultPhraseKey: extras?.zeroResultPhraseKey === null ? null : "creative",
    diagnostics: {
      original_query: original,
      normalized_query: normalized,
      generated_variants_count: variantsCount,
      matched_field: null,
      matched_alias: extras?.didYouMean ?? extras?.skuSuggestion ?? null,
      correction_type: extras?.correctionType ?? null,
      final_confidence: extras?.confidence ?? 0,
      result_count: 0,
      zero_result_reason: reason,
    },
  };
}

function searchProductsCore(query: string, products: SearchableProduct[]): SearchResult {
  const original = String(query ?? "").trim();
  const normalized = foldToken(original);
  const variants = tokenizeQuery(original);

  if (!original) {
    return emptySearch(original, normalized, variants.length, "empty_query", { zeroResultPhraseKey: null });
  }

  // Soft suggestion свк → сік: never auto-force juice/CBD results.
  const soft = querySuggestionHit(original);
  if (soft) {
    const evidenceCount = products.filter((p) => {
      const blob = foldToken(
        [p.title, p.titleRu, p.categoryName, p.brand, p.search].filter(Boolean).join(" "),
      );
      return soft.evidenceNeedles.some((n) => blob.includes(foldToken(n)));
    }).length;
    return emptySearch(original, normalized, variants.length, "soft_suggestion_only", {
      didYouMean: soft.to,
      confidence: evidenceCount > 0 ? 55 : 40,
      correctionType: "soft_query_suggestion",
    });
  }

  // Category alias with zero evidence → zero results (not fuzzy expansion).
  if (categoryAliasHit(original)) {
    const scored: SearchHit[] = [];
    for (const p of products) {
      const hit = scoreProduct(original, p);
      if (hit && hit.score >= MIN_ACCEPT_SCORE) scored.push(hit);
    }
    scored.sort((a, b) => b.score - a.score || a.productId.localeCompare(b.productId));
    const top = scored[0];
    const second = scored[1];
    const accepted =
      top && (!second || top.score - second.score >= MIN_MARGIN || top.score >= 88)
        ? scored.filter((h) => h.score >= MIN_ACCEPT_SCORE && h.score >= (top.score - 12))
        : top && top.score >= 88
          ? scored.filter((h) => h.score >= 88)
          : [];
    if (accepted.length === 0) {
      return emptySearch(original, normalized, variants.length, "category_alias_no_evidence");
    }
    const byId = new Map(products.map((p) => [p.id, p]));
    return {
      hits: accepted,
      products: accepted.map((h) => byId.get(h.productId)!).filter(Boolean),
      didYouMean: accepted[0]?.matchedAlias ?? null,
      skuSuggestion: null,
      zeroResultPhraseKey: null,
      diagnostics: {
        original_query: original,
        normalized_query: normalized,
        generated_variants_count: variants.length,
        matched_field: accepted[0]?.matchedField ?? null,
        matched_alias: accepted[0]?.matchedAlias ?? null,
        correction_type: accepted[0]?.correctionType ?? null,
        final_confidence: accepted[0]?.score ?? 0,
        result_count: accepted.length,
        zero_result_reason: null,
      },
    };
  }

  const scored: SearchHit[] = [];
  for (const p of products) {
    const hit = scoreProduct(original, p);
    if (hit) scored.push(hit);
  }
  scored.sort((a, b) => b.score - a.score || a.productId.localeCompare(b.productId));

  const top = scored[0];
  const second = scored[1];
  if (!top || top.score < MIN_ACCEPT_SCORE) {
    const skuSuggestion = suggestSkuOneDigitTypo(original, products);
    if (skuSuggestion) {
      return emptySearch(original, normalized, variants.length, "sku_one_digit_suggestion", {
        skuSuggestion,
        confidence: 70,
        correctionType: "sku_hamming_1",
      });
    }
    const brandGuess = resolveCuratedBrandAlias(original);
    return emptySearch(original, normalized, variants.length, top ? "below_confidence_threshold" : "no_field_evidence", {
      didYouMean: brandGuess && brandGuess.toLowerCase() !== original.toLowerCase() ? brandGuess : null,
      confidence: top?.score ?? 0,
    });
  }
  if (second && top.score - second.score < MIN_MARGIN && top.score < 88) {
    // 168D5 — short prefix autocomplete may legitimately tie (кі → Kinder + Kikkoman).
    const shortCluster =
      top.correctionType === "short_prefix" ||
      top.correctionType === "short_alias" ||
      top.correctionType === "short_brand_alias" ||
      top.correctionType === "short_brand_prefix" ||
      top.correctionType === "short_title_word_prefix" ||
      top.correctionType === "short_title_edge";
    // Ambiguous weak cluster → zero results unless exact-class scores or short prefix cluster.
    if (!shortCluster && top.score < 86) {
      return emptySearch(original, normalized, variants.length, "insufficient_margin", {
        didYouMean: top.matchedAlias ?? null,
        confidence: top.score,
      });
    }
  }

  const floor = Math.max(MIN_ACCEPT_SCORE, top.score - 12);
  const accepted = scored.filter((h) => h.score >= floor && h.matchedField !== "description");
  // Never keep description-only winners.
  const cleaned = accepted.filter((h) => !(h.matchedField === "description" && h.score < MIN_ACCEPT_SCORE));
  if (cleaned.length === 0) {
    return emptySearch(original, normalized, variants.length, "description_only_rejected", {
      didYouMean: top.matchedAlias ?? null,
      confidence: top.score,
    });
  }

  const byId = new Map(products.map((p) => [p.id, p]));
  const didYouMean =
    cleaned[0]?.correctionType &&
    cleaned[0].matchedAlias &&
    foldToken(cleaned[0].matchedAlias) !== normalized
      ? cleaned[0].matchedAlias
      : resolveCuratedBrandAlias(original) &&
          foldToken(resolveCuratedBrandAlias(original)!) !== normalized
        ? resolveCuratedBrandAlias(original)
        : null;

  return {
    hits: cleaned,
    products: cleaned.map((h) => byId.get(h.productId)!).filter(Boolean),
    didYouMean,
    skuSuggestion: null,
    zeroResultPhraseKey: null,
    diagnostics: {
      original_query: original,
      normalized_query: normalized,
      generated_variants_count: variants.length,
      matched_field: cleaned[0]?.matchedField ?? null,
      matched_alias: cleaned[0]?.matchedAlias ?? null,
      correction_type: cleaned[0]?.correctionType ?? null,
      final_confidence: cleaned[0]?.score ?? 0,
      result_count: cleaned.length,
      zero_result_reason: null,
    },
  };
}

/**
 * Suggestion contract: retrieval and "did you mean" must agree on one canonical
 * query. A suggestion is only ever surfaced if re-running this SAME search with
 * the suggested text actually returns products — otherwise a user clicking the
 * suggestion would land on an equally empty result (ping-pong / broken promise).
 */
export function searchProducts(
  query: string,
  products: SearchableProduct[],
  _verifySuggestion = true,
): SearchResult {
  const result = searchProductsCore(query, products);
  if (!_verifySuggestion || result.products.length > 0) return result;

  let didYouMean = result.didYouMean;
  if (didYouMean && searchProducts(didYouMean, products, false).products.length === 0) {
    didYouMean = null;
  }
  let skuSuggestion = result.skuSuggestion;
  if (skuSuggestion && searchProducts(skuSuggestion, products, false).products.length === 0) {
    skuSuggestion = null;
  }
  if (didYouMean === result.didYouMean && skuSuggestion === result.skuSuggestion) return result;
  return {
    ...result,
    didYouMean,
    skuSuggestion,
    diagnostics: { ...result.diagnostics, matched_alias: didYouMean ?? skuSuggestion ?? null },
  };
}

/**
 * COMMERCE-LAUNCH-168D5 / SEARCH-PERF-169A — autocomplete suggestions from normalized length >= 2.
 * Uses the same searchProducts engine (no second index). Caps visible rows.
 */
export type SearchSuggestionKind = "brand" | "product" | "sku" | "category";

export type SearchSuggestion = {
  kind: SearchSuggestionKind;
  label: string;
  query: string;
  productId?: string;
  brand?: string | null;
  imageUrl?: string | null;
  priceUah?: number | null;
  matchReason?: string | null;
};

export function suggestSearchAutocomplete(
  query: string,
  products: SearchableProduct[],
  limit = 5,
): SearchSuggestion[] {
  const q = String(query ?? "").trim();
  if (compactToken(q).length < 2) return [];
  // Exact identifiers: never run typo/fuzzy expansions for pure numeric/barcode SKUs beyond engine rules.
  const result = searchProducts(q, products);
  const out: SearchSuggestion[] = [];
  const seen = new Set<string>();

  const push = (s: SearchSuggestion) => {
    const key = `${s.kind}:${s.query.toLowerCase()}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(s);
  };

  const brandAlias = resolveCuratedBrandAlias(q);
  if (brandAlias) {
    push({ kind: "brand", label: brandAlias, query: brandAlias, matchReason: "exact_alias" });
  }

  // Product rows first (with thumbs). Brand/category once each — avoid brand-spam filling limit.
  for (const p of result.products) {
    if (out.length >= limit) break;
    const sku = String(p.sku ?? "").trim();
    if (sku && (sku === q || sku.startsWith(q))) {
      push({
        kind: "sku",
        label: sku,
        query: sku,
        productId: p.id,
        brand: p.brand ?? null,
        imageUrl: p.imageUrl ?? null,
        matchReason: "exact_sku",
      });
    }
    const title = String(p.title ?? "").trim();
    if (title) {
      push({
        kind: "product",
        label: title.length > 72 ? `${title.slice(0, 69)}…` : title,
        query: title,
        productId: p.id,
        brand: p.brand ?? null,
        imageUrl: p.imageUrl ?? null,
        priceUah: typeof p.priceUah === "number" ? p.priceUah : null,
        matchReason: result.diagnostics.matched_field ?? "product",
      });
    }
  }
  for (const p of result.products) {
    if (out.length >= limit) break;
    const brand = String(p.brand ?? "").trim();
    if (brand) {
      push({
        kind: "brand",
        label: brand,
        query: brand,
        brand,
        imageUrl: p.imageUrl ?? null,
        matchReason: "brand",
      });
    }
    const cat = String(p.categoryName ?? "").trim();
    if (cat && compactToken(cat).includes(compactToken(q)) && compactToken(q).length >= 3) {
      push({
        kind: "category",
        label: cat,
        query: cat,
        matchReason: "category_prefix",
      });
    }
  }

  return out.slice(0, limit);
}

/** Backward-compatible predicate used by older call sites. */
export function matchesTokens(haystackFolded: string, tokens: string[]): boolean {
  if (tokens.length === 0) return true;
  // Rebuild a pseudo product from haystack for legacy filters.
  const product: SearchableProduct = {
    id: "legacy",
    sku: "",
    title: haystackFolded,
    search: haystackFolded,
  };
  // Join tokens roughly back to a query when possible.
  const query = tokens[0] ?? "";
  const hit = scoreProduct(query, product);
  return Boolean(hit && hit.score >= MIN_ACCEPT_SCORE);
}
